import { Extension } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey, Selection, type Transaction } from "@tiptap/pm/state";
import { isHistoryTransaction } from "@tiptap/pm/history";
import type { Mapping } from "@tiptap/pm/transform";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export type ReviewRange = { from: number; to: number };
export type ReviewChange = ReviewRange & { id: number; deleted: boolean; deletedText?: string };
export type SummaryReviewGroup = ReviewRange & { id: number; changeIds: number[] };
export type SummaryReviewState = {
  visible: boolean;
  highlighted?: boolean;
  active: boolean;
  scope: "selection" | "document";
  pending: ReviewRange[];
  changes: ReviewChange[];
  selected: number | null;
  nextId: number;
  stopped?: boolean;
};
export const emptySummaryReview = (): SummaryReviewState => ({ visible: false, active: false, scope: "document", pending: [], changes: [], selected: null, nextId: 1 });
export const summaryReviewKey = new PluginKey<SummaryReviewState>("summaryReview");
export const summaryStreamTransaction = "summaryStreamTransaction";
type ReviewAction = { start: ReviewRange; scope: SummaryReviewState["scope"] } | { restore: SummaryReviewState }
  | { lock: ReviewRange[] } | { release: ReviewRange[]; changes: Omit<ReviewChange, "id">[]; base?: SummaryReviewState }
  | { select: number } | { clear: true } | { highlighted: boolean } | { finish: true };
export const reviewTransaction = (tr: Transaction, action: ReviewAction) => tr.setMeta(summaryReviewKey, action).setMeta("addToHistory", false);

const overlaps = (a: ReviewRange, b: ReviewRange) => b.from === b.to ? a.from < b.from && a.to > b.from : a.from < b.to && a.to > b.from;
const mapped = <T extends ReviewRange>(range: T, mapping: Mapping): T => {
  const from = mapping.map(range.from, 1);
  return { ...range, from, to: Math.max(from, mapping.map(range.to, -1)) };
};
function mergeRanges(ranges: ReviewRange[]): ReviewRange[] {
  return ranges.filter(range => range.to > range.from).sort((a, b) => a.from - b.from).reduce<ReviewRange[]>((result, range) => {
    const last = result[result.length - 1];
    if (last && last.to >= range.from) last.to = Math.max(last.to, range.to);
    else result.push({ ...range });
    return result;
  }, []);
}
function subtract(ranges: ReviewRange[], released: ReviewRange[]) {
  for (const release of released) ranges = ranges.flatMap(range => !overlaps(range, release) ? [range] : [
    { from: range.from, to: Math.min(range.to, release.from) },
    { from: Math.max(range.from, release.to), to: range.to },
  ].filter(part => part.to > part.from));
  return ranges;
}

export const mapSummaryReview = (state: SummaryReviewState, mapping: Mapping): SummaryReviewState => ({ ...state,
  pending: mergeRanges(state.pending.map(range => mapped(range, mapping))),
  changes: state.changes.map(range => mapped(range, mapping)),
});

/** Check every step, including formatting, table operations, paste and native undo. */
export function touchesSummaryLock(transaction: Transaction, pending: ReviewRange[]) {
  let ranges = pending;
  for (let index = 0; index < transaction.steps.length; index++) {
    const step = transaction.steps[index];
    const json = step.toJSON();
    let affected: ReviewRange[] = [];
    if (typeof json.from === "number" && typeof json.to === "number") affected = [{ from: json.from, to: json.to }];
    else if (typeof json.pos === "number") affected = [{ from: json.pos, to: json.pos + (transaction.docs[index].nodeAt(json.pos)?.nodeSize ?? 1) }];
    else step.getMap().forEach((from, to) => affected.push({ from, to }));
    const currentRanges = ranges;
    if (!affected.length || affected.some(range => currentRanges.some(lock => overlaps(lock, range)))) return ranges.length > 0;
    ranges = ranges.map(range => mapped(range, transaction.mapping.slice(index, index + 1)));
  }
  return false;
}

function applyReview(transaction: Transaction, state: SummaryReviewState): SummaryReviewState {
  const action = transaction.getMeta(summaryReviewKey) as ReviewAction | undefined;
  if (action && "restore" in action) return action.restore;
  if (action && "start" in action) return { ...emptySummaryReview(), visible: true, active: true, scope: action.scope, pending: action.start.to > action.start.from ? [action.start] : [] };
  if (action && "clear" in action) return state.active ? state : emptySummaryReview();
  if (action && "finish" in action) return { ...state, active: false, pending: [] };
  if (action && "highlighted" in action) return { ...state, highlighted: action.highlighted };
  if (!state.visible) return state;
  let next = state;
  if (transaction.docChanged) next = { ...state,
    pending: mergeRanges(state.pending.map(range => mapped(range, transaction.mapping))),
    changes: state.changes.filter(range => state.active || !isHistoryTransaction(transaction) || !touchesSummaryLock(transaction, [range])).map(range => mapped(range, transaction.mapping)),
  };
  if (action && "lock" in action) next = { ...next, pending: mergeRanges([...next.pending, ...action.lock]) };
  if (action && "release" in action) {
    // A returned target includes unchanged words too; release the entire acknowledged passage.
    const released = action.release.map(range => ({ from: transaction.mapping.map(range.from, -1), to: transaction.mapping.map(range.to, 1) }));
    next = action.base ?? next;
    next = { ...next, pending: subtract(next.pending, released), changes: [...next.changes] };
    for (const change of action.changes) {
      const adjacent = next.changes.filter(old => old.from <= change.to && old.to >= change.from);
      next.changes = next.changes.filter(old => !adjacent.includes(old));
      next.changes.push({ ...change, from: Math.min(change.from, ...adjacent.map(old => old.from)),
        to: Math.max(change.to, ...adjacent.map(old => old.to)), deleted: change.deleted || adjacent.some(old => old.deleted), id: adjacent[0]?.id ?? next.nextId++ });
    }
    next.changes.sort((a, b) => a.from - b.from);
  }
  if (action && "select" in action) next = { ...next, selected: action.select };
  return next;
}

type ReviewLabels = { pending: string; changed: string; deleted: string };
function reviewBadgePosition(doc: Node, position: number) {
  const resolved = doc.resolve(position);
  if (resolved.parent.isTextblock) return position;
  // Structural changes can start between table cells or list items, where a
  // span would be invalid HTML. Anchor their marker in the nearest text block.
  return (Selection.findFrom(resolved, 1, true) ?? Selection.findFrom(resolved, -1, true))?.from ?? position;
}

const reviewGroupCache = new WeakMap<ReviewChange[], { doc: Node; groups: SummaryReviewGroup[] }>();
/** Count and navigate the continuous highlighted surfaces, retaining exact diffs for undo and locks. */
export function summaryReviewGroups(doc: Node, state: SummaryReviewState): SummaryReviewGroup[] {
  const cached = reviewGroupCache.get(state.changes);
  if (cached?.doc === doc) return cached.groups;
  const groups: SummaryReviewGroup[] = [];
  const ranges = state.changes.map(change => {
    const anchor = doc.resolve(reviewBadgePosition(doc, change.from));
    const endPosition = Math.max(change.from, change.to - 1);
    const end = doc.resolve(endPosition);
    const lastText = end.parent.isTextblock ? end : Selection.findFrom(end, -1, true)?.$from;
    return { id: change.id, changeIds: [change.id],
      from: Math.min(change.from, anchor.parent.isTextblock ? anchor.before() : anchor.pos),
      to: Math.max(change.to, anchor.parent.isTextblock ? anchor.after() : anchor.pos,
        lastText?.parent.isTextblock ? lastText.after() : endPosition),
    };
  }).sort((a, b) => a.from - b.from || a.to - b.to);
  for (const range of ranges) {
    const previous = groups[groups.length - 1];
    let separated = false;
    if (previous && previous.to < range.from) {
      doc.nodesBetween(previous.to, range.from, node => {
        // Container boundaries between adjacent list items/cells are not a gap.
        // An unchanged paragraph, heading, empty block or divider is a gap.
        if (node.isTextblock || (node.isBlock && node.isAtom)) separated = true;
        return !separated;
      });
    }
    if (previous && !separated) {
      previous.to = Math.max(previous.to, range.to);
      previous.changeIds.push(...range.changeIds);
    } else groups.push(range);
  }
  reviewGroupCache.set(state.changes, { doc, groups });
  return groups;
}

function selectedReviewChanges(doc: Node, state: SummaryReviewState) {
  return state.selected === null ? [] : summaryReviewGroups(doc, state).find(group => group.changeIds.includes(state.selected!))?.changeIds ?? [];
}

export function summaryReviewDecorations(doc: Node, state: SummaryReviewState, labels: ReviewLabels) {
  if (!state.visible || state.highlighted === false) return DecorationSet.empty;
  const decorations: Decoration[] = [];
  const selected = new Set(selectedReviewChanges(doc, state));
  const badge = (position: number, label: string, kind: string, id?: number, detail?: string) => decorations.push(Decoration.widget(position, () => {
    const span = document.createElement("span");
    span.className = `summary-review-badge summary-review-badge--${kind}`;
    span.textContent = label;
    span.title = detail ? `${label}: ${detail}` : label;
    if (id !== undefined) span.dataset.reviewChange = String(id);
    span.setAttribute("aria-label", span.title);
    return span;
  }, { side: -1, key: `${kind}-${id ?? position}-${label}-${detail ?? ""}`, ignoreSelection: true }));
  const changeAnchors = new Map(state.changes.map(change => [change.id, reviewBadgePosition(doc, change.from)]));
  const indicatedLocks = new Set<ReviewRange>();
  doc.descendants((node, pos) => {
    if (!node.isBlock) return;
    // A fully locked list, table or field is one surface, rather than a lock on
    // every nested paragraph/cell. Partial locks still decorate their text blocks.
    const wholeLock = state.pending.find(range => range.from <= pos && range.to >= pos + node.nodeSize);
    if (!node.isTextblock && !node.isAtom && !wholeLock) return;
    const from = node.isTextblock ? pos + 1 : pos;
    const to = node.isTextblock ? pos + node.nodeSize - 1 : pos + node.nodeSize;
    const lock = wholeLock ?? state.pending.find(range => (range.from < to && range.to > from) || (from === to && range.from <= from && range.to >= to));
    const changes = state.changes.filter(range => (range.from < to && range.to > from) || (changeAnchors.get(range.id)! >= from && changeAnchors.get(range.id)! <= to));
    const classes = ["summary-review-block"];
    if (lock) {
      classes.push("summary-review-pending-block");
      if (!indicatedLocks.has(lock)) classes.push("summary-review-lock-start");
      indicatedLocks.add(lock);
    }
    if (changes.length) classes.push("summary-review-changed-block");
    if (changes.some(change => selected.has(change.id))) classes.push("is-current");
    if (lock || changes.length) decorations.push(Decoration.node(pos, pos + node.nodeSize, {
      class: classes.join(" "), title: lock ? labels.pending : labels.changed,
    }));
    return false;
  });
  // Keep exact range anchors for navigation and lock feedback. Their background
  // is painted once on the block, so wrapped lines no longer look like stripes.
  for (const range of state.pending) if (range.to > range.from) decorations.push(Decoration.inline(range.from, range.to, { class: "summary-review-pending", title: labels.pending }));
  for (const change of state.changes) {
    const active = selected.has(change.id) ? " is-current" : "";
    if (change.to > change.from) decorations.push(Decoration.inline(change.from, change.to, { class: `summary-review-changed ai-change${active}`, title: labels.changed, "data-review-change": String(change.id) }));
    badge(changeAnchors.get(change.id)!, change.deleted ? labels.deleted : labels.changed, `${change.deleted ? "deleted" : "changed"}${active}`, change.id, change.deletedText);
  }
  return DecorationSet.create(doc, decorations);
}

export function createSummaryReviewPlugin(options?: { labels: () => ReviewLabels; blocked: () => void }) {
  const decorations = new WeakMap<SummaryReviewState, DecorationSet>();
  return new Plugin<SummaryReviewState>({
    key: summaryReviewKey,
    state: { init: emptySummaryReview, apply(transaction, state) {
      const next = applyReview(transaction, state);
      const previous = decorations.get(state);
      // Selection changes reuse decorations; typing maps them instead of scanning a long report.
      if (previous && next !== state && transaction.docChanged && !transaction.getMeta(summaryReviewKey) && !isHistoryTransaction(transaction)) {
        const beforeSelection = selectedReviewChanges(transaction.before, state);
        const afterSelection = selectedReviewChanges(transaction.doc, next);
        if (beforeSelection.length === afterSelection.length && beforeSelection.every(id => afterSelection.includes(id))) {
          decorations.set(next, previous.map(transaction.mapping, transaction.doc));
        }
      }
      return next;
    } },
    filterTransaction(transaction, state) {
      const pending = summaryReviewKey.getState(state)?.pending ?? [];
      const streamed = transaction.getMeta(summaryStreamTransaction) || transaction.getMeta("appendedTransaction")?.getMeta(summaryStreamTransaction);
      if (transaction.docChanged && !streamed && touchesSummaryLock(transaction, pending)) {
        options?.blocked();
        return false;
      }
      return true;
    },
    props: {
      decorations: state => {
        const review = summaryReviewKey.getState(state)!;
        let result = decorations.get(review);
        if (!result) {
          result = summaryReviewDecorations(state.doc, review, options?.labels() ?? { pending: "Wacht op AI", changed: "Aangepast", deleted: "Tekst verwijderd" });
          decorations.set(review, result);
        }
        return result;
      },
      handleDOMEvents: { beforeinput(view, event) {
        if (!(event as InputEvent).inputType?.startsWith("insert")) return false;
        const pending = summaryReviewKey.getState(view.state)?.pending ?? [];
        if (!pending.some(range => overlaps(range, view.state.selection))) return false;
        event.preventDefault(); options?.blocked(); return true;
      } },
    },
  });
}
export const SummaryReview = Extension.create<{ labels: () => ReviewLabels; blocked: () => void }>({
  name: "summaryReview",
  addProseMirrorPlugins() { return [createSummaryReviewPlugin(this.options)]; },
});

/** Text matching spans inline formatting and nested lists/tables, retaining document positions. */
export function summaryTargetRanges(doc: Node, target: Node): ReviewRange[] {
  let text = "";
  const positions: number[] = [];
  doc.descendants((node, pos) => {
    if (node.isTextblock && text) { text += "\n"; positions.push(pos); }
    if (node.isText) for (let index = 0; index < node.text!.length; index++) { text += node.text![index]; positions.push(pos + index); }
  });
  const needle = target.textBetween(0, target.content.size, "\n").trim();
  if (!needle) return [];
  const ranges: ReviewRange[] = [];
  const pattern = new RegExp(needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  // Markdown with explicit formatting must not unlock an unrelated plain-text duplicate.
  const requirements: { offset: number; node: Node; ancestors: Node[] }[] = [];
  let offset = 0;
  target.descendants((node, pos) => {
    if (node.isTextblock && offset) offset++;
    if (!node.isText) return;
    const resolved = target.resolve(pos);
    const ancestors: Node[] = [];
    for (let depth = 1; depth <= resolved.depth; depth++) if (resolved.node(depth).type.name !== "paragraph") ancestors.push(resolved.node(depth));
    if (node.marks.length || ancestors.length) requirements.push({ offset, node, ancestors });
    offset += node.text!.length;
  });
  for (const match of text.matchAll(pattern)) {
    const matchesFormatting = requirements.every(requirement => {
      for (let index = 0; index < requirement.node.text!.length; index++) {
        const position = positions[match.index! + requirement.offset + index];
        if (position === undefined) return false;
        const actual = doc.nodeAt(position);
        if (!requirement.node.marks.every(mark => actual?.marks.some(other => mark.eq(other)))) return false;
        const resolved = doc.resolve(position);
        if (!requirement.ancestors.every(ancestor => {
          for (let depth = 1; depth <= resolved.depth; depth++) {
            const parent = resolved.node(depth);
            if (parent.type === ancestor.type && (parent.type.name !== "heading" || parent.attrs.level === ancestor.attrs.level)) return true;
          }
          return false;
        })) return false;
      }
      return true;
    });
    if (matchesFormatting) ranges.push({ from: positions[match.index!], to: positions[match.index! + match[0].length - 1] + 1 });
  }
  return ranges;
}
